import type { components } from "./generated/schema";

export type ApiErrorPayload = components["schemas"]["ApiError"];

export class ApiRequestError extends Error {
  constructor(
    public readonly payload: ApiErrorPayload,
    public readonly status: number,
  ) {
    super(payload.message);
    this.name = "ApiRequestError";
  }
}

export function csrfToken(): string {
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("csrftoken="));
  return cookie ? decodeURIComponent(cookie.slice("csrftoken=".length)) : "";
}

async function errorPayload(response: Response): Promise<ApiErrorPayload> {
  try {
    return (await response.json()) as ApiErrorPayload;
  } catch {
    return {
      code: response.status === 413 ? "request_too_large" : "http_error",
      message:
        response.status === 413
          ? "Le fichier dépasse une limite HTTP/proxy avant d'arriver à Omni Portal."
          : `Erreur HTTP ${response.status}.`,
      details: {},
      request_id: response.headers.get("X-Request-ID") ?? "",
    };
  }
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<{ data: T; response: Response }> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.method && init.method !== "GET" && init.method !== "HEAD") {
    const token = csrfToken();
    if (token) headers.set("X-CSRFToken", token);
  }
  if (typeof init.body === "string")
    headers.set("Content-Type", "application/json");
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers,
  });
  if (!response.ok && response.status !== 304) {
    throw new ApiRequestError(await errorPayload(response), response.status);
  }
  const data =
    response.status === 204 || response.status === 304
      ? (undefined as T)
      : ((await response.json()) as T);
  return { data, response };
}
